import type {
  CreditRateServiceSetting,
  CreditRateServiceSettingInput,
  CreditRateSettingsView,
  CreditRateUsdSetting,
  PutCreditRateSettingsRequest,
  ServiceUsageRateKind,
} from '../../../../shared-types/src/index.js';

const UNITS: Record<ServiceUsageRateKind, CreditRateServiceSetting['unitName']> = {
  web_search: 'request',
  image_input: 'image',
  image_output: 'image',
  video_output: 'second',
};
const GENERATION_MODELS: Partial<Record<ServiceUsageRateKind, ReadonlySet<string>>> = {
  image_output: new Set(['openai/gpt-image-1', 'openai/gpt-image-2']),
  video_output: new Set(['sora-2']),
};

export interface RateEvidenceDraft {
  sourceUrl: string;
  verifiedAt: string;
  note: string;
}

export interface ServiceRateDraft extends RateEvidenceDraft {
  kind: ServiceUsageRateKind;
  rateKey: string;
  unitName: CreditRateServiceSetting['unitName'];
  costMicroUsdPerUnit: string;
  marginMultiplier: string;
  provisional: boolean;
}

export interface UsdPerCreditDraft extends RateEvidenceDraft {
  value: string;
}

export interface RateSettingsDraft {
  serviceRates: ServiceRateDraft[];
  usdPerCredit?: UsdPerCreditDraft;
}

export interface RateFormError {
  code:
    'credit_rate_evidence_required' | 'credit_rate_evidence_invalid' | 'invalid_margin_multiplier';
  field?: string;
}

export interface RateSettingsDiff {
  serviceRates: CreditRateServiceSettingInput[];
  removeServiceRates: NonNullable<PutCreditRateSettingsRequest['removeServiceRates']>;
  usdPerCredit?: CreditRateUsdSetting;
}

type Validation<T, K extends string> =
  ({ ok: true } & Record<K, T>) | { ok: false; error: RateFormError };

function invalid(field?: string): { ok: false; error: RateFormError } {
  return {
    ok: false,
    error: field
      ? { code: 'credit_rate_evidence_invalid', field }
      : { code: 'credit_rate_evidence_invalid' },
  };
}

function noteValue(note: string): { note?: string } {
  return note ? { note } : {};
}

function decimalValue(raw: string, positive: boolean): number | undefined {
  const value = raw.trim();
  if (!value || !/^\d+(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(value)) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || (positive && parsed === 0)) return undefined;
  const match = String(parsed).match(/^(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i);
  if (!match) return undefined;
  const decimalPlaces = Math.max(0, (match[2]?.length ?? 0) - Number(match[3] ?? 0));
  return decimalPlaces <= 12 ? parsed : undefined;
}

function validateEvidence(
  input: RateEvidenceDraft,
  now: number
): Validation<Pick<CreditRateUsdSetting, 'sourceUrl' | 'verifiedAt' | 'note'>, 'evidence'> {
  if (!input.sourceUrl.trim() || !input.verifiedAt.trim()) {
    return { ok: false, error: { code: 'credit_rate_evidence_required' } };
  }
  try {
    const source = new URL(input.sourceUrl);
    if (source.protocol !== 'http:' && source.protocol !== 'https:') return invalid('sourceUrl');
  } catch {
    return invalid('sourceUrl');
  }
  const verifiedAt = Date.parse(input.verifiedAt);
  if (
    !/^\d{4}-\d{2}-\d{2}T/.test(input.verifiedAt) ||
    Number.isNaN(verifiedAt) ||
    verifiedAt > now
  ) {
    return invalid('verifiedAt');
  }
  if (input.note.length > 1_000) return invalid('note');
  return {
    ok: true,
    evidence: {
      sourceUrl: input.sourceUrl,
      verifiedAt: input.verifiedAt,
      ...noteValue(input.note),
    },
  };
}

export function validateServiceRow(
  input: ServiceRateDraft,
  now = Date.now()
): Validation<CreditRateServiceSettingInput, 'row'> {
  if (!(input.kind in UNITS) || input.unitName !== UNITS[input.kind]) return invalid('unitName');
  if (input.rateKey.length > 256 || !/^[a-z0-9][a-z0-9._:=|/-]*$/.test(input.rateKey)) {
    return invalid('rateKey');
  }
  if (
    (input.kind === 'image_output' || input.kind === 'video_output') &&
    input.rateKey === 'default'
  ) {
    return invalid('rateKey');
  }
  const allowedModels = GENERATION_MODELS[input.kind];
  if (allowedModels) {
    const separator = input.rateKey.indexOf('|');
    const model = separator > 0 ? input.rateKey.slice(0, separator) : '';
    if (separator === input.rateKey.length - 1 || !allowedModels.has(model))
      return invalid('rateKey');
  }
  const costInput = input.costMicroUsdPerUnit.trim();
  const costMicroUsdPerUnit = Number(costInput);
  if (
    !/^\d+$/.test(costInput) ||
    !Number.isSafeInteger(costMicroUsdPerUnit) ||
    costMicroUsdPerUnit < 0
  ) {
    return invalid('costMicroUsdPerUnit');
  }
  const marginMultiplier = decimalValue(input.marginMultiplier, true);
  if (marginMultiplier === undefined) {
    return { ok: false, error: { code: 'invalid_margin_multiplier' } };
  }
  const evidence = validateEvidence(input, now);
  if (!evidence.ok) return evidence;
  return {
    ok: true,
    row: {
      kind: input.kind,
      rateKey: input.rateKey,
      unitName: input.unitName,
      costMicroUsdPerUnit,
      marginMultiplier,
      provisional: input.provisional,
      ...evidence.evidence,
    },
  };
}

export function validateUsdPerCredit(
  input: UsdPerCreditDraft,
  now = Date.now()
): Validation<CreditRateUsdSetting, 'value'> {
  const value = decimalValue(input.value, true);
  if (value === undefined) return invalid('usdPerCredit');
  const evidence = validateEvidence(input, now);
  if (!evidence.ok) return evidence;
  return { ok: true, value: { value, ...evidence.evidence } };
}

function serviceKey(row: Pick<CreditRateServiceSetting, 'kind' | 'rateKey'>): string {
  return `${row.kind}:${row.rateKey}`;
}

function sameServiceRate(
  left: CreditRateServiceSettingInput,
  right: CreditRateServiceSetting | undefined,
): boolean {
  return Boolean(
    right &&
      left.kind === right.kind &&
      left.rateKey === right.rateKey &&
      left.unitName === right.unitName &&
      left.costMicroUsdPerUnit === right.costMicroUsdPerUnit &&
      (left.marginMultiplier ?? 1) === right.marginMultiplier &&
      (left.provisional ?? false) === right.provisional &&
      left.sourceUrl === right.sourceUrl &&
      left.verifiedAt === right.verifiedAt &&
      (left.note ?? '') === (right.note ?? ''),
  );
}

function sameServiceRateDraft(
  left: ServiceRateDraft,
  right: CreditRateServiceSetting | undefined,
): boolean {
  return Boolean(
    right &&
      left.kind === right.kind &&
      left.rateKey === right.rateKey &&
      left.unitName === right.unitName &&
      left.costMicroUsdPerUnit === String(right.costMicroUsdPerUnit) &&
      left.marginMultiplier === String(right.marginMultiplier) &&
      left.provisional === right.provisional &&
      left.sourceUrl === right.sourceUrl &&
      left.verifiedAt === right.verifiedAt &&
      left.note === (right.note ?? ''),
  );
}

function sameUsdSetting(
  left: CreditRateUsdSetting,
  right: CreditRateUsdSetting | undefined,
): boolean {
  return Boolean(
    right &&
      left.value === right.value &&
      left.sourceUrl === right.sourceUrl &&
      left.verifiedAt === right.verifiedAt &&
      (left.note ?? '') === (right.note ?? ''),
  );
}

function sameUsdDraft(
  left: UsdPerCreditDraft,
  right: CreditRateUsdSetting | undefined,
): boolean {
  return Boolean(
    right &&
      left.value === String(right.value) &&
      left.sourceUrl === right.sourceUrl &&
      left.verifiedAt === right.verifiedAt &&
      left.note === (right.note ?? ''),
  );
}

export function diffRateSettings(
  loaded: CreditRateSettingsView,
  draft: RateSettingsDraft,
  now = Date.now()
): RateSettingsDiff {
  const loadedByKey = new Map(loaded.serviceRates.map((row) => [serviceKey(row), row]));
  const draftKeys = new Set(draft.serviceRates.map(serviceKey));
  if (draftKeys.size !== draft.serviceRates.length) {
    throw new Error('credit_rate_evidence_invalid:rateKey');
  }
  const serviceRates: CreditRateServiceSettingInput[] = [];
  for (const input of draft.serviceRates) {
    const existing = loadedByKey.get(serviceKey(input));
    if (sameServiceRateDraft(input, existing)) continue;
    const result = validateServiceRow(input, now);
    if (!result.ok)
      throw new Error(`${result.error.code}${result.error.field ? `:${result.error.field}` : ''}`);
    if (!sameServiceRate(result.row, existing)) serviceRates.push(result.row);
  }
  const removeServiceRates = loaded.serviceRates
    .filter((row) => !draftKeys.has(serviceKey(row)))
    .map(({ kind, rateKey }) => ({ kind, rateKey }));
  const diff: RateSettingsDiff = { serviceRates, removeServiceRates };
  if (draft.usdPerCredit && !sameUsdDraft(draft.usdPerCredit, loaded.usdPerCredit)) {
    const result = validateUsdPerCredit(draft.usdPerCredit, now);
    if (!result.ok)
      throw new Error(`${result.error.code}${result.error.field ? `:${result.error.field}` : ''}`);
    if (!sameUsdSetting(result.value, loaded.usdPerCredit)) diff.usdPerCredit = result.value;
  }
  return diff;
}

export function summarizeChanges(diff: RateSettingsDiff): {
  serviceRateCount: number;
  usdPerCreditChanged: boolean;
} {
  return {
    serviceRateCount: diff.serviceRates.length + diff.removeServiceRates.length,
    usdPerCreditChanged: diff.usdPerCredit !== undefined,
  };
}

export function draftFromRateSettings(settings: CreditRateSettingsView): RateSettingsDraft {
  return {
    serviceRates: settings.serviceRates.map((row) => ({
      kind: row.kind,
      rateKey: row.rateKey,
      unitName: row.unitName,
      costMicroUsdPerUnit: String(row.costMicroUsdPerUnit),
      marginMultiplier: String(row.marginMultiplier),
      provisional: row.provisional,
      sourceUrl: row.sourceUrl,
      verifiedAt: row.verifiedAt,
      note: row.note ?? '',
    })),
    ...(settings.usdPerCredit
      ? {
          usdPerCredit: {
            value: String(settings.usdPerCredit.value),
            sourceUrl: settings.usdPerCredit.sourceUrl,
            verifiedAt: settings.usdPerCredit.verifiedAt,
            note: settings.usdPerCredit.note ?? '',
          },
        }
      : {}),
  };
}
